import { useState, useEffect, useLayoutEffect, useCallback, useRef, type CSSProperties, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, X, Plus, Settings2, Undo2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { QuickActionCategory } from '@hector/shared-types/quick-actions';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { AddPromptForm } from './AddPromptForm';
import { iconForPrompt } from './quickActionIcons';
import { isNativePrompt, type CreateUserPromptInput, type UserPrompt } from '../../api/userPrompts.api';

interface QuickActionsGridProps {
  /** Actions d'UNE colonne, mémoïsées par l'appelant (l'effet de resynchronisation dépend de leur identité). */
  prompts: UserPrompt[];
  isLoading: boolean;
  onPromptClick: (promptContent: string) => void;
  onDeletePrompt: (id: string) => Promise<void>;
  onReorderPrompts: (items: { id: string; sort_order: number }[]) => Promise<void>;
  onCreatePrompt: (data: CreateUserPromptInput) => Promise<UserPrompt>;
  onUpdatePrompt: (id: string, data: { title?: string; description?: string; prompt_content?: string }) => Promise<UserPrompt>;
  onSetPromptHidden: (id: string, hidden: boolean) => Promise<UserPrompt>;
  /** Libellé de la colonne. */
  title?: string;
  /** Colonne où « Ajouter une action » range l'action créée. */
  category?: QuickActionCategory;
  /**
   * Nombre de cartes visibles d'un coup (ex: 3 pour aligner les colonnes) ; au-delà,
   * la colonne défile, en vue repliée comme dans « Gérer ». undefined = colonne sans plafond.
   */
  visibleCardCount?: number;
  /**
   * Titres à faire remonter en premier dans la vue réduite (ex: garder
   * "Synthèse du dossier" visible même si son sort_order n'est pas le plus
   * petit) — n'affecte que l'ordre d'affichage hors édition, jamais l'ordre
   * réel manipulé par le glisser-déposer.
   */
  priorityTitles?: string[];
  /**
   * Carte de la vue repliée. Le panneau y branche les comportements propres
   * à une colonne (sélecteur de dossier, choix du type d'acte) ; sans elle,
   * un clic préremplit le composer avec le prompt.
   */
  renderCollapsedCard?: (prompt: UserPrompt) => ReactNode;
}

// ─── Sortable card with wiggle + soft-delete / hide ───────────────────────────

interface SortableCardProps {
  prompt: UserPrompt;
  index: number;
  isEditing: boolean;
  isPendingDelete: boolean;
  isHidden: boolean;
  onMarkDelete: (id: string) => void;
  onUndoDelete: (id: string) => void;
  onToggleHidden: (prompt: UserPrompt) => void;
  onEdit: (prompt: UserPrompt) => void;
  onClick: () => void;
}

function SortableCard({
  prompt,
  index,
  isEditing,
  isPendingDelete,
  isHidden,
  onMarkDelete,
  onUndoDelete,
  onToggleHidden,
  onEdit,
  onClick,
}: SortableCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: prompt.id, disabled: isPendingDelete });

  const dndStyle = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    zIndex: isDragging ? 10 : undefined,
    position: 'relative' as const,
  };

  // Stagger phase so cards wiggle out of sync
  const wiggleDelay = (index * 0.07) % 0.4;
  const wiggleDuration = 0.55 + (index % 3) * 0.07;

  const shouldWiggle = isEditing && !isDragging && !isPendingDelete;
  const native = isNativePrompt(prompt);
  const Icon = iconForPrompt(prompt);

  return (
    <div
      ref={setNodeRef}
      style={dndStyle}
      className="h-full"
      {...(isEditing && !isPendingDelete ? { ...attributes, ...listeners } : {})}
    >
      <motion.div
        animate={shouldWiggle ? { rotate: [-0.4, 0.4, -0.4] } : { rotate: 0 }}
        transition={
          shouldWiggle
            ? { duration: wiggleDuration, repeat: Infinity, ease: 'easeInOut', delay: wiggleDelay }
            : { duration: 0.1 }
        }
        className="relative h-full"
        style={{ transformOrigin: 'center 60%' }}
      >
        <button
          type="button"
          onClick={() => { if (!isEditing) onClick(); }}
          disabled={isEditing}
          className={[
            'flex w-full h-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-all',
            isPendingDelete
              ? 'border-red-100 bg-red-50 cursor-default opacity-50'
              : 'border-grey-200 hover:border-grey-300 hover:bg-grey-50 cursor-pointer disabled:cursor-grab disabled:opacity-100 disabled:bg-white',
            // Une native masquée reste dans « Gérer » pour pouvoir être réaffichée.
            isHidden ? 'border-dashed opacity-50' : '',
          ].join(' ')}
        >
          <span
            aria-hidden="true"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-grey-100"
          >
            <Icon className="h-4 w-4 text-charcoal" />
          </span>
          <span className="min-w-0">
            <span className={[
              'block truncate text-xs font-medium',
              isPendingDelete ? 'line-through text-grey-400' : 'text-charcoal',
            ].join(' ')}>
              {prompt.title}
            </span>
            {prompt.description && (
              <span className="mt-0.5 block truncate text-xs text-grey-500">{prompt.description}</span>
            )}
          </span>
        </button>

        {/* Badges édition. Native Hector : un seul œil (masquer / réafficher) —
            son texte appartient au catalogue, le serveur refuse toute modification.
            Autres actions : crayon (modifier) + croix/undo (supprimer), le crayon
            masqué quand la carte est marquée pour suppression. Taille 20px : à
            16px, deux pastilles côte-à-côte étaient indiscernables. */}
        {isEditing && (
          <div className="absolute top-1 right-1 flex items-center gap-1 z-20">
            {native ? (
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleHidden(prompt);
                }}
                className="w-5 h-5 bg-grey-400 rounded-full flex items-center justify-center hover:bg-grey-600 transition-colors shadow-sm"
                aria-label={isHidden ? `Afficher ${prompt.title}` : `Masquer ${prompt.title}`}
                aria-pressed={isHidden}
              >
                {isHidden
                  ? <EyeOff className="w-2.5 h-2.5 text-white" />
                  : <Eye className="w-2.5 h-2.5 text-white" />}
              </button>
            ) : (
              <>
                {!isPendingDelete && (
                  <button
                    type="button"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(prompt);
                    }}
                    className="w-5 h-5 bg-grey-400 rounded-full flex items-center justify-center hover:bg-grey-600 transition-colors shadow-sm"
                    aria-label={`Modifier ${prompt.title}`}
                  >
                    <Pencil className="w-2.5 h-2.5 text-white" />
                  </button>
                )}
                <button
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isPendingDelete) onUndoDelete(prompt.id);
                    else onMarkDelete(prompt.id);
                  }}
                  className="w-5 h-5 bg-grey-400 rounded-full flex items-center justify-center hover:bg-grey-600 transition-colors shadow-sm"
                  aria-label={isPendingDelete ? `Annuler la suppression de ${prompt.title}` : `Supprimer ${prompt.title}`}
                >
                  {isPendingDelete
                    ? <Undo2 className="w-2.5 h-2.5 text-white" />
                    : <X className="w-2.5 h-2.5 text-white" />
                  }
                </button>
              </>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}

/**
 * Les actions créées par l'avocat (`is_default: false`) toujours en tête de
 * colonne, dans « Gérer » comme en vue repliée (retour du 2026-09-25 : une
 * action personnalisée, récente ou ancienne, doit rester sous les yeux). Tri
 * stable : chaque groupe garde l'ordre choisi par l'avocat.
 */
function customActionsFirst(prompts: UserPrompt[]): UserPrompt[] {
  return [...prompts].sort((a, b) => Number(a.is_default) - Number(b.is_default));
}

/**
 * Ordre de la vue repliée : les actions personnalisées de l'avocat
 * (`is_default: false` — celles qu'il a lui-même créées) passent TOUJOURS
 * devant les actions par défaut du catalogue (retour du 2026-09-10 — un
 * avocat qui crée sa propre action rapide veut la voir sans cliquer sur
 * "Gérer"), puis les titres de `priorityTitles` départagent le reste.
 * À égalité, le tri stable conserve l'ordre choisi par l'avocat (sort_order).
 */
function sortByPriority(prompts: UserPrompt[], priorityTitles: string[]): UserPrompt[] {
  const priorityIndex = new Map(priorityTitles.map((title, i) => [title, i]));
  return [...prompts].sort((a, b) => {
    const customRank = Number(a.is_default) - Number(b.is_default); // false (0) avant true (1)
    if (customRank !== 0) return customRank;
    const ai = priorityIndex.get(a.title) ?? Number.POSITIVE_INFINITY;
    const bi = priorityIndex.get(b.title) ?? Number.POSITIVE_INFINITY;
    if (ai === bi) return 0;
    return ai - bi;
  });
}

// ─── Main grid ─────────────────────────────────────────────────────────────────

const DEFAULT_SKELETON_COUNT = 6;

/**
 * Invitation à faire défiler une colonne qui déborde (retour du 2026-09-25 :
 * la barre de défilement n'apparaît qu'au survol, rien ne signalait les
 * actions suivantes). La zone visible laisse dépasser le haut de la carte
 * suivante, et ce bord s'estompe en fondu : la colonne se lit comme « à
 * suivre ». Le fondu du bas disparaît en fin de liste, celui du haut apparaît
 * dès qu'on a défilé.
 */
const NEXT_CARD_PEEK_PX = 30;
const SCROLL_FADE_PX = NEXT_CARD_PEEK_PX;

function scrollFadeStyle(
  maxHeight: number,
  edges: { moreAbove: boolean; moreBelow: boolean },
): CSSProperties {
  const top = edges.moreAbove ? `transparent 0, #000 ${SCROLL_FADE_PX}px` : '#000 0';
  const bottom = edges.moreBelow
    ? `#000 calc(100% - ${SCROLL_FADE_PX}px), transparent 100%`
    : '#000 100%';
  const mask = `linear-gradient(to bottom, ${top}, ${bottom})`;
  // Le masque s'applique à la boîte de la liste, pas à son contenu défilé :
  // le fondu reste collé aux bords pendant le défilement.
  return { maxHeight, maskImage: mask, WebkitMaskImage: mask };
}

function errorText(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Erreur inconnue';
}

export function QuickActionsGrid({
  prompts,
  isLoading,
  onPromptClick,
  onDeletePrompt,
  onReorderPrompts,
  onCreatePrompt,
  onUpdatePrompt,
  onSetPromptHidden,
  title = 'Actions rapides',
  category = 'analyse',
  visibleCardCount,
  priorityTitles,
  renderCollapsedCard,
}: QuickActionsGridProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [localPrompts, setLocalPrompts] = useState<UserPrompt[]>(() => customActionsFirst(prompts));
  // IDs marqués pour suppression — suppression effective uniquement au "Terminer"
  const [pendingDeletes, setPendingDeletes] = useState<Set<string>>(new Set());
  // Natives dont la visibilité change — appliqué au "Terminer", annulable comme
  // les suppressions. Valeur = état masqué visé.
  const [pendingVisibility, setPendingVisibility] = useState<Map<string, boolean>>(new Map());
  const [showAddModal, setShowAddModal] = useState(false);
  // Prompt en cours d'édition — null = modale fermée, instance = modale ouverte avec ce prompt.
  // Un seul état pour éviter la désync "modale ouverte mais sans données".
  const [editingPrompt, setEditingPrompt] = useState<UserPrompt | null>(null);

  useEffect(() => {
    if (!isEditing) setLocalPrompts(customActionsFirst(prompts));
  }, [prompts, isEditing]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const leaveEditMode = useCallback(() => {
    setIsEditing(false);
    setPendingDeletes(new Set());
    setPendingVisibility(new Map());
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = localPrompts.findIndex((p) => p.id === active.id);
      const newIndex = localPrompts.findIndex((p) => p.id === over.id);
      // Une action de l'avocat reste au-dessus des actions Hector même si on
      // glisse une native par-dessus : le glisser-déposer ordonne chaque groupe.
      const reordered = customActionsFirst(arrayMove(localPrompts, oldIndex, newIndex));
      setLocalPrompts(reordered);
      // Seuls les ids de CETTE colonne partent : le serveur réécrit leurs
      // positions sans toucher aux autres colonnes.
      try {
        await onReorderPrompts(reordered.map((p, i) => ({ id: p.id, sort_order: i })));
      } catch (error) {
        // Le hook a déjà relancé la lecture de la bibliothèque ; sortir de
        // l'édition laisse l'effet de resynchronisation afficher l'ordre réel.
        toast.error(`Impossible d'enregistrer le nouvel ordre : ${errorText(error)}`);
        leaveEditMode();
      }
    },
    [localPrompts, onReorderPrompts, leaveEditMode],
  );

  const handleMarkDelete = useCallback((id: string) => {
    setPendingDeletes((prev) => new Set([...prev, id]));
  }, []);

  const handleUndoDelete = useCallback((id: string) => {
    setPendingDeletes((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const isHidden = useCallback(
    (prompt: UserPrompt) => pendingVisibility.get(prompt.id) ?? prompt.is_hidden === true,
    [pendingVisibility],
  );

  const handleToggleHidden = useCallback((prompt: UserPrompt) => {
    setPendingVisibility((prev) => {
      const next = new Map(prev);
      const target = !(prev.get(prompt.id) ?? prompt.is_hidden === true);
      if (target === (prompt.is_hidden === true)) next.delete(prompt.id);
      else next.set(prompt.id, target);
      return next;
    });
  }, []);

  const handleCreate = useCallback(
    async (data: { title: string; description?: string; prompt_content: string }) => {
      const newPrompt = await onCreatePrompt({ ...data, category });
      // Le serveur la range en fin de colonne ; l'avocat veut la voir tout de
      // suite : elle passe en tête, et cet ordre est enregistré.
      const reordered = [newPrompt, ...localPrompts];
      setLocalPrompts(reordered);
      setShowAddModal(false);
      try {
        await onReorderPrompts(reordered.map((p, i) => ({ id: p.id, sort_order: i })));
      } catch (error) {
        toast.error(`Action créée, mais impossible de la placer en tête : ${errorText(error)}`);
      }
    },
    [onCreatePrompt, onReorderPrompts, category, localPrompts],
  );

  const handleUpdate = useCallback(
    async (data: { title: string; description?: string; prompt_content: string }) => {
      if (!editingPrompt) return;
      const updated = await onUpdatePrompt(editingPrompt.id, data);
      // Refléter immédiatement la modif dans la grille locale (sans attendre le refetch parent)
      setLocalPrompts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setEditingPrompt(null);
    },
    [editingPrompt, onUpdatePrompt],
  );

  const handleExitEdit = useCallback(async () => {
    const deletes = [...pendingDeletes];
    const visibilityChanges = [...pendingVisibility];
    leaveEditMode();
    try {
      for (const id of deletes) {
        await onDeletePrompt(id);
      }
      for (const [id, hidden] of visibilityChanges) {
        await onSetPromptHidden(id, hidden);
      }
    } catch (error) {
      toast.error(`Certaines modifications n'ont pas été enregistrées : ${errorText(error)}`);
    }
    setLocalPrompts(customActionsFirst(prompts));
  }, [pendingDeletes, pendingVisibility, leaveEditMode, onDeletePrompt, onSetPromptHidden, prompts]);

  const handleCancelEdit = useCallback(() => {
    leaveEditMode();
    setLocalPrompts(customActionsFirst(prompts));
  }, [leaveEditMode, prompts]);

  const hasPendingDeletes = pendingDeletes.size > 0;

  // Hors édition : toutes les actions sauf les natives masquées, dans l'ordre
  // de sortByPriority. "Gérer" révèle toute la colonne, masquées comprises,
  // pour réordonner/ajouter/masquer. items de SortableContext doit rester égal
  // à ce qui est rendu.
  const shownPrompts = localPrompts.filter((p) => p.is_hidden !== true);
  const visiblePrompts = isEditing
    ? localPrompts
    : sortByPriority(shownPrompts, priorityTitles ?? []);
  const skeletonCount = visibleCardCount ?? DEFAULT_SKELETON_COUNT;
  // Les cartes de la vue repliée fournies par l'appelant ne sont pas triables :
  // leurs ids ne doivent pas figurer dans SortableContext, sinon dnd-kit suit des
  // ids sans nœud triable monté (crash insertBefore/removeChild documenté dans
  // apps/legalworkspace/CLAUDE.md). items = exactement les SortableCard rendues.
  const rendersSortableCards = isEditing || !renderCollapsedCard;
  const sortableIds = rendersSortableCards ? visiblePrompts.map((p) => p.id) : [];

  // Au-delà de visibleCardCount cartes, la colonne défile au lieu de tronquer
  // la liste (retour du 2026-09-25 : les actions suivantes étaient
  // inatteignables hors « Gérer »). La hauteur visible est MESURÉE sur la
  // dernière carte visible plutôt que codée en dur : une action sans
  // description est plus basse d'une ligne, et une valeur fixe laisserait
  // dépasser la carte suivante ou couperait la dernière. offsetTop ignore les
  // transform (glisser-déposer, ondulation de « Gérer »), d'où la liste en
  // `relative` : elle devient l'offsetParent de ses cartes.
  const listRef = useRef<HTMLDivElement>(null);
  const [listMaxHeight, setListMaxHeight] = useState<number | null>(null);
  const [scrollEdges, setScrollEdges] = useState({ moreAbove: false, moreBelow: false });
  const renderedLayoutKey = isLoading
    ? `loading:${skeletonCount}`
    : `${isEditing ? 'edit' : 'view'}:${visiblePrompts.map((p) => p.id).join(',')}`;

  const updateScrollEdges = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const next = {
      moreAbove: list.scrollTop > 1,
      moreBelow: list.scrollTop + list.clientHeight < list.scrollHeight - 1,
    };
    setScrollEdges((prev) =>
      prev.moreAbove === next.moreAbove && prev.moreBelow === next.moreBelow ? prev : next,
    );
  }, []);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || visibleCardCount == null) return;
    const measure = () => {
      const lastVisibleCard = list.children.item(visibleCardCount - 1);
      setListMaxHeight(
        list.children.length > visibleCardCount && lastVisibleCard instanceof HTMLElement
          ? lastVisibleCard.offsetTop + lastVisibleCard.offsetHeight + NEXT_CARD_PEEK_PX
          : null,
      );
    };
    measure();
    // Une carte peut changer de hauteur sans que la liste change (police
    // chargée après coup, largeur de fenêtre) : on observe les cartes elles-mêmes.
    const observer = new ResizeObserver(measure);
    for (const card of Array.from(list.children)) observer.observe(card);
    return () => observer.disconnect();
  }, [visibleCardCount, renderedLayoutKey]);

  // Les bords ne sont connus qu'une fois la hauteur plafonnée appliquée.
  useLayoutEffect(updateScrollEdges, [updateScrollEdges, listMaxHeight, renderedLayoutKey]);

  // data-quick-action-column : ancre stable des tests E2E, indépendante de la structure du DOM.
  return (
    <div className="w-full" data-quick-action-column={category}>
      {/* Section header — hauteur alignée sur les titres texte-seul des colonnes
          voisines : text-xs fait une ligne de 16px (h-4), donc le bouton "Gérer"
          perd tout padding vertical pour ne pas gonfler la ligne et décaler les
          cartes de cette colonne par rapport aux deux autres (retour du 2026-09-10). */}
      <div className="mb-2 flex h-4 items-center justify-between">
        <span className="text-xs font-semibold text-grey-400 uppercase tracking-wider">
          {title}
        </span>
        <AnimatePresence mode="wait">
          {isEditing ? (
            <motion.div
              key="edit-actions"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="flex items-center gap-1"
            >
              <button
                type="button"
                onClick={handleCancelEdit}
                className="text-xs text-grey-400 hover:text-grey-600 transition-colors px-1.5 py-0 rounded-md hover:bg-grey-100"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleExitEdit}
                className={[
                  'text-xs font-semibold transition-colors px-1.5 py-0 rounded-md',
                  hasPendingDeletes
                    ? 'text-red-600 hover:bg-red-50'
                    : 'text-charcoal hover:text-grey-700 hover:bg-grey-100',
                ].join(' ')}
              >
                {hasPendingDeletes ? `Supprimer (${pendingDeletes.size})` : 'Terminer'}
              </button>
            </motion.div>
          ) : (
            <motion.button
              key="manage"
              type="button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={() => setIsEditing(true)}
              aria-label={`Gérer les actions rapides : ${title}`}
              className="flex items-center gap-1 text-xs text-grey-400 hover:text-grey-700 transition-colors px-1.5 py-0 rounded-md hover:bg-grey-100"
            >
              <Settings2 className="w-3 h-3" />
              Gérer
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Sortable grid */}
      <div className="relative">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
            <div
              ref={listRef}
              data-quick-action-list=""
              data-more-below={scrollEdges.moreBelow || undefined}
              data-more-above={scrollEdges.moreAbove || undefined}
              onScroll={updateScrollEdges}
              style={listMaxHeight == null ? undefined : scrollFadeStyle(listMaxHeight, scrollEdges)}
              className={[
                'relative grid grid-cols-1 gap-2',
                // overscroll-contain : arrivé en bas de la colonne, la molette ne
                // fait pas défiler toute la page d'accueil.
                listMaxHeight == null ? '' : 'overflow-y-auto overscroll-contain pr-1',
              ].join(' ')}
            >
              {isLoading
                ? Array.from({ length: skeletonCount }).map((_, i) => (
                    <div key={i} className="h-[52px] rounded-lg bg-grey-100 animate-pulse" />
                  ))
                : visiblePrompts.map((prompt, i) => (
                    !rendersSortableCards && renderCollapsedCard
                      ? <div key={prompt.id}>{renderCollapsedCard(prompt)}</div>
                      : (
                        <SortableCard
                          key={prompt.id}
                          prompt={prompt}
                          index={i}
                          isEditing={isEditing}
                          isPendingDelete={pendingDeletes.has(prompt.id)}
                          isHidden={isHidden(prompt)}
                          onMarkDelete={handleMarkDelete}
                          onUndoDelete={handleUndoDelete}
                          onToggleHidden={handleToggleHidden}
                          onEdit={setEditingPrompt}
                          onClick={() => onPromptClick(prompt.prompt_content)}
                        />
                      )
                  ))}
            </div>
          </SortableContext>
        </DndContext>

        {/* "Ajouter une action" vit HORS du conteneur scrollable ci-dessus : à
            l'intérieur, il fallait dérouler toute la colonne pour l'atteindre,
            donc il passait inaperçu (retour du 2026-09-25). Rendu sans
            AnimatePresence pour éviter le conflit removeChild entre Framer
            Motion (exit animation) et dnd-kit. */}
        {isEditing && (
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-grey-300 py-2 text-xs font-medium text-grey-500 transition-colors hover:border-charcoal hover:bg-grey-50 hover:text-charcoal"
          >
            <Plus className="h-3.5 w-3.5" />
            Ajouter une action
          </button>
        )}
      </div>

      {/* Colonne sans aucune carte visible (tout supprimé, ou toutes les natives
          masquées) : un raccourci vers la création plutôt qu'un vide muet. */}
      {!isLoading && !isEditing && visiblePrompts.length === 0 && (
        <button
          type="button"
          onClick={() => { setIsEditing(true); setShowAddModal(true); }}
          className="w-full py-4 border border-dashed border-grey-200 rounded-lg text-xs text-grey-400 hover:text-grey-600 hover:border-grey-300 transition-colors"
        >
          + Ajouter une action
        </button>
      )}

      <Dialog open={showAddModal} onOpenChange={(open) => { if (!open) setShowAddModal(false); }}>
        <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden" hideCloseButton>
          <div className="px-5 py-4 border-b border-grey-100">
            <h3 className="text-sm font-semibold text-grey-900">Nouvelle action rapide : {title}</h3>
          </div>
          <div className="px-5 py-4">
            <AddPromptForm
              onSave={handleCreate}
              onCancel={() => setShowAddModal(false)}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Modale d'édition — réutilise AddPromptForm avec initialValues.
          key={editingPrompt.id} force le remount du form quand on passe d'un prompt à l'autre,
          sinon les useState internes garderaient les valeurs du précédent. */}
      <Dialog open={editingPrompt !== null} onOpenChange={(open) => { if (!open) setEditingPrompt(null); }}>
        <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden" hideCloseButton>
          <div className="px-5 py-4 border-b border-grey-100">
            <h3 className="text-sm font-semibold text-grey-900">Modifier l'action rapide</h3>
          </div>
          <div className="px-5 py-4">
            {editingPrompt && (
              <AddPromptForm
                key={editingPrompt.id}
                initialValues={{
                  title: editingPrompt.title,
                  description: editingPrompt.description ?? '',
                  prompt_content: editingPrompt.prompt_content,
                }}
                onSave={handleUpdate}
                onCancel={() => setEditingPrompt(null)}
                saveLabel="Sauvegarder"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
